/**
 * Block: site-frame, viewScript.
 *
 * Enqueued on front end.
 */

// inert attribute polyfill.
import 'wicg-inert';

import lottie from 'lottie-web';
import Swiper, { A11y, Controller, Keyboard, Navigation } from 'swiper';

const preloadedEvent = new Event( 'preloaded' );

/**
 * Site preloader.
 */
function preload() {
	//console.log( 'preload' );
	const preloader = document.querySelector( '#site-preloader' );

	// Check cookie to see if preloader has already run.
	const cookie = document.cookie.split( ';' ).find( ( c ) => {
		return c.trim().startsWith( 'preloaded=' );
	} );
	if ( cookie ) {
		preloader.classList.remove( 'preloading' );
		document.body.classList.add( 'preloaded' );
		//console.log( 'cookie: dispatching preloaded event' );
		window.dispatchEvent( preloadedEvent );
		return;
	}

	const animEl = preloader.querySelector( '.logo-animation' );

	// Load lottie animation.
	const logoAnimation = lottie.loadAnimation( {
		container: animEl,
		renderer: 'svg',
		loop: false,
		autoplay: true,
		path: animEl.dataset.src,
	} );

	// On animation complete, set preloaded class.
	logoAnimation.addEventListener(
		'complete',
		() => {
			//console.log( 'animation complete' );
			preloader.classList.add( 'preloaded' );
			preloader.classList.remove( 'preloading' );

			// Add preloaded class to body after expected CSS animation duration (1.5s).
			setTimeout( () => {
				document.body.classList.add( 'preloaded' );
				//console.log( 'dispatching preloaded event' );
				window.dispatchEvent( preloadedEvent );
			}, 1500 );

			// Set cookie for this session so preloader doesn't run again.
			document.cookie = 'preloaded=true; path=/';
		},
		{ once: true }
	);
}

preload();

/**
 * Site slider.
 */
const swiper = new Swiper( '.wp-block-buildings-food-site-frame__slider', {
	modules: [ A11y, Controller, Keyboard, Navigation ],
	slidesPerView: 1,
	spaceBetween: 0,
	//loop: true,
	a11y: {
		prevSlideMessage: 'Previous site',
		nextSlideMessage: 'Next site',
		itemRoleDescriptionMessage:
			'Website associated with Buildings and Food',
		lastSlideMessage: 'This is the last site (for now)',
		slideRole: 'region',
	},
	keyboard: {
		enabled: true,
	},
	navigation: {
		nextEl: '#next',
		prevEl: '#prev',
	},
	preloadImages: false,
	lazy: {
		checkInView: true,
		enabled: true,
		loadOnTransitionStart: true,
		scrollingElement: '.wp-block-buildings-food-site-frame__slider',
	},
} );

// Disallow tabbing to slide elements not in view.
swiper.slides.map( ( slide, index ) => {
	if ( index !== swiper.activeIndex ) {
		slide.inert = true;
	}
} );

swiper.on( 'activeIndexChange', () => {
	// Unload iframes when not in view.
	const prevSlide = swiper.slides[ swiper.previousIndex ];
	const prevIframe = prevSlide.querySelector( 'iframe' );
	if ( prevIframe && prevIframe.classList.contains( 'iframe-loaded' ) ) {
		prevIframe.dataset.src = prevIframe.src;
		prevIframe.src = '';
		prevIframe.classList.remove( 'iframe-loaded' );
	}
	// No tabbing to elements not in view.
	prevSlide.inert = true;

	// Allow tabbing to slide elements in view.
	const activeSlide = swiper.slides[ swiper.activeIndex ];
	activeSlide.inert = false;

	// Load active slide iframe.
	const activeIframe = activeSlide.querySelector( 'iframe' );
	if (
		activeIframe &&
		! activeIframe.classList.contains( 'iframe-loaded' )
	) {
		activeIframe.addEventListener(
			'load',
			() => {
				activeIframe.classList.add( 'iframe-loaded' );
			},
			{ once: true }
		);
		activeIframe.src = activeIframe.dataset.src;
	}
} );

const pagination = new Swiper(
	'.wp-block-buildings-food-site-frame__site-list',
	{
		modules: [ A11y, Controller ],
		slidesPerView: 'auto',
		spaceBetween: 0, // fixes bug causing later slides not centered.
		centeredSlides: true,
		grabCursor: true,
		slideToClickedSlide: true,
		a11y: {
			prevSlideMessage: 'Previous site',
			nextSlideMessage: 'Next site',
			itemRoleDescriptionMessage:
				'Website associated with Buildings and Food',
			lastSlideMessage: 'This is the last site (for now)',
			slideRole: 'region',
		},
		controller: {
			control: swiper,
		},
	}
);

// Main slider also controls pagination slider.
swiper.controller.control = pagination;

// Attempt to refresh pagination slider after preload animation.
function refreshPagination( event ) {
	//console.log( 'preloaded', event );

	setTimeout( () => {
		//console.log( 'refreshing pagination' );
		// Event fires twice (???) so check if pagination element exists.
		if ( pagination.el.querySelectorAll ) {
			pagination.update();
			//console.log( 'pagination refreshed' );
		}
	}, 1000 );
}
window.addEventListener( 'preloaded', refreshPagination );
